import { supabase } from '@/lib/supabase'
import type { SubscriptionTier, UserProfile } from '@/types/user'
import { FREE_TIER_SESSION_LIMIT } from '@/types/user'
import { create } from 'zustand'

export interface UserState {
  profile: UserProfile | null
  loading: boolean
  error: string | null
}

interface UserActions {
  fetchProfile: () => Promise<void>
  clear: () => void
}

export const useUserStore = create<UserState & UserActions>((set) => ({
  profile: null,
  loading: false,
  error: null,

  fetchProfile: async () => {
    set({ loading: true, error: null })

    // RLS scopes this query to the authenticated user — no client-side user_id filter
    const { data, error } = await supabase.from('profiles').select('*').single()

    if (error || !data) {
      set({ loading: false, error: 'Failed to load profile.' })
      return
    }

    const profile: UserProfile = {
      id: data.id,
      email: data.email ?? null,
      name: data.name,
      targetRole: data.target_role,
      experienceLevel: data.experience_level,
      targetIndustry: data.target_industry,
      subscriptionTier: data.subscription_tier,
      sessionsUsedThisMonth: data.sessions_used_this_month,
      isAdmin: data.is_admin ?? false,
      giftedProExpiresAt: data.gifted_pro_expires_at ?? null,
      sessionWindowStartsAt: data.session_window_starts_at,
    }

    set({ profile, loading: false })
  },

  clear: () => set({ profile: null, loading: false, error: null }),
}))

// Derived selectors — pass directly to useUserStore(selector) to avoid unnecessary re-renders

export function selectTier(state: UserState): SubscriptionTier {
  return state.profile?.subscriptionTier ?? 'free'
}

export function selectIsPro(state: UserState): boolean {
  return selectTier(state) === 'pro'
}

export function selectSessionsRemaining(state: UserState): number {
  const used = state.profile?.sessionsUsedThisMonth ?? 0
  return selectIsPro(state) ? Number.POSITIVE_INFINITY : Math.max(0, FREE_TIER_SESSION_LIMIT - used)
}

export function selectIsAtLimit(state: UserState): boolean {
  if (selectIsPro(state)) return false
  return (state.profile?.sessionsUsedThisMonth ?? 0) >= FREE_TIER_SESSION_LIMIT
}
